import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Label,
  ListInput,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  useStepperReturn,
} from '@ais-ui/ui-core';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, UseFormReturn } from 'react-hook-form';
import { StepperFooter } from './StepperFooter';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  MiscellaneousField,
  PrefilledField,
  PrefilledFieldsMap,
} from '../../reportsTypes';
import { createDynamicMiscellaneousZodSchema } from '../utils/createDynamicMiscellaneousZodSchema';
import { FieldEnabledState } from '../Miscellaneous/MiscellaneousStringItem';
import { ReportTemplateMiscellaneousValues } from '../Miscellaneous/MiscellaneousBooleanItem';
import { StepperHeadline } from './StepperHeadline';
import { PiFlag, PiPhone } from 'react-icons/pi';
import { useLocalization } from '@ais-ui/locale-management';
import { groupFieldsByDependency } from '../utils/groupedFields';
import { FieldGroupRenderer } from '../Miscellaneous/FieldGroupRenderer';
import { useShapeFetch } from '@ais-ui/api';
import { DateRange } from 'react-day-picker';
import { GroupMultiSelect } from '../../../Groups/GroupMultiSelect';
import { useCreateReportContext } from './CreateReportContext';
import { v4 as uuidv4 } from 'uuid';
import { useGetReportExportFormats } from '@ais-ui/websockets';
import { getMailActionOptions } from '../utils/mailActionMapping';
interface PrefillSelectProps {
  prefilledFieldsMap: PrefilledFieldsMap;
  form: ReturnType<typeof useForm>;
}

const PrefillSelect = ({ prefilledFieldsMap, form }: PrefillSelectProps) => {
  const { messages } = useLocalization();

  const onSelect = (value: string) => {
    const prefillObject = prefilledFieldsMap[value];
    prefillObject.fields.forEach((field: PrefilledField) => {
      form.setValue(field.fieldName, field.fieldValue);
    });
  };

  return (
    <div className="mb-6 space-y-2">
      <div className="flex justify-between">
        <Label>
          {messages.reportsCreateReportMiscellaneousPrefillValuesLabel}
        </Label>
        <InfoIconTooltip className="mr-2">
          {messages.reportsCreateReportMiscellaneousPrefillValuesTooltip}
        </InfoIconTooltip>
      </div>
      <Select onValueChange={onSelect}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Select profile" />
        </SelectTrigger>

        <SelectContent>
          {Object.entries(prefilledFieldsMap).map(([key, prefillObject]) => (
            <SelectItem key={key} value={key}>
              {prefillObject.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

function getColorIntervalFromColorBy(colorBy: string) {
  switch (colorBy) {
    case 'cog':
      return [
        { id: uuidv4(), color: '#3ea908', startInterval: 0, endInterval: 180 },
        {
          id: uuidv4(),
          color: '#e50000',
          startInterval: 180,
          endInterval: 360,
        },
      ];

    case 'sog':
      return [
        { id: uuidv4(), color: '#ff0000', startInterval: 0, endInterval: 1 },
        { id: uuidv4(), color: '#ff6600', startInterval: 1, endInterval: 5 },
        { id: uuidv4(), color: '#ffcc00', startInterval: 5, endInterval: 10 },
        { id: uuidv4(), color: '#ffff00', startInterval: 10, endInterval: 15 },
        { id: uuidv4(), color: '#00ff00', startInterval: 15, endInterval: 25 },
        { id: uuidv4(), color: '#00cc00', startInterval: 25, endInterval: 40 },
        {
          id: uuidv4(),
          color: '#00ff00',
          startInterval: 40,
          endInterval: 102.3,
        },
      ];

    case 'draught':
      return [
        { id: uuidv4(), color: '#ff0000', startInterval: 0, endInterval: 7 },
        { id: uuidv4(), color: '#ff6600', startInterval: 7, endInterval: 9 },
        { id: uuidv4(), color: '#ffcc00', startInterval: 9, endInterval: 11 },
        { id: uuidv4(), color: '#ffff00', startInterval: 11, endInterval: 13 },
        { id: uuidv4(), color: '#00ff00', startInterval: 13, endInterval: 15 },
        {
          id: uuidv4(),
          color: '#00cc00',
          startInterval: 15,
          endInterval: 25.5,
        },
      ];

    case 'length':
      return [
        { id: uuidv4(), color: '#ff0000', startInterval: 0, endInterval: 5 },
        { id: uuidv4(), color: '#ff6600', startInterval: 5, endInterval: 20 },
        { id: uuidv4(), color: '#ffcc00', startInterval: 20, endInterval: 50 },
        {
          id: uuidv4(),
          color: '#ffff00',
          startInterval: 50,
          endInterval: 1022,
        },
      ];

    case 'width':
      return [
        { id: uuidv4(), color: '#ff0000', startInterval: 0, endInterval: 5 },
        { id: uuidv4(), color: '#ff6600', startInterval: 5, endInterval: 10 },
        { id: uuidv4(), color: '#ffcc00', startInterval: 10, endInterval: 30 },
        { id: uuidv4(), color: '#ffff00', startInterval: 30, endInterval: 126 },
      ];

    case 'volume':
      return [
        { id: uuidv4(), color: '#ff0000', startInterval: 0, endInterval: 100 },
        {
          id: uuidv4(),
          color: '#ff6600',
          startInterval: 100,
          endInterval: 500,
        },
        {
          id: uuidv4(),
          color: '#ffcc00',
          startInterval: 500,
          endInterval: 2000,
        },
        {
          id: uuidv4(),
          color: '#ffff00',
          startInterval: 2000,
          endInterval: 4000,
        },
        {
          id: uuidv4(),
          color: '#00ff00',
          startInterval: 4000,
          endInterval: 7000000,
        },
      ];

    default:
      return [];
  }
}

function useWatchColorBy(form: UseFormReturn<any>) {
  const colorByValue = form.watch('colorBy');

  useEffect(() => {
    if (colorByValue !== undefined && colorByValue !== null) {
      const newColorByIntervalValue = getColorIntervalFromColorBy(colorByValue);

      // Update the 'colorByInterval' field with the new value
      form.setValue('colorByInterval', newColorByIntervalValue);
    } else {
      form.setValue('colorByInterval', []);
    }
  }, [colorByValue, form]);
}

export const MiscellaneousStep = () => {
  const { messages } = useLocalization();

  const {
    stepperProps,
    miscellaneous,
    setMiscellaneous,
    chosenReportType,
    dateRange,
    // Add shared form state from context (only the values, not the setters)
    email,
    phone,
    countryCode,
    selectedGroups,
  } = useCreateReportContext();

  // Fetch available export formats for this report type
  const { data: exportFormats } = useGetReportExportFormats(
    chosenReportType?.type || ''
  );

  // Get mail action options based on available export formats
  const mailActionOptions = useMemo(
    () => getMailActionOptions(exportFormats),
    [exportFormats]
  );

  const miscellaneousFields = useMemo(
    () => chosenReportType?.miscellaneous as Record<string, MiscellaneousField>,
    [chosenReportType],
  );
  const prefilledFieldsMap = useMemo(
    () => chosenReportType?.prefilledFieldsMap,
    [chosenReportType],
  );
  const miscellaneousSchema = useCallback(
    () => createDynamicMiscellaneousZodSchema(miscellaneousFields),
    [miscellaneousFields],
  )();
  const shapeFetch = useShapeFetch();

  const [watchedValues, setWatchedValues] = useState<Record<string, string>>(
    {},
  );
  const defaultValues = useMemo<Record<string, any>>(() => {
    // Always prioritize miscellaneous from context if it has values
    if (Object.keys(miscellaneous).length > 0) {
      // Merge with defaults to ensure all fields are present
      const defaults = miscellaneousFields
        ? Object.keys(miscellaneousFields).reduce(
            (acc, key) => {
              const field = miscellaneousFields[key];
              if (field.defaultValue !== undefined) {
                acc[key] = field.defaultValue;
              }
              return acc;
            },
            {} as Record<string, any>,
          )
        : {};

      // Override defaults with context values
      const result = {
        ...defaults,
        ...miscellaneous,
        // Ensure shared fields are included
        email: miscellaneous.email ?? email ?? [],
        phone: miscellaneous.phone ?? phone ?? '',
        countryCode: miscellaneous.countryCode ?? countryCode ?? '',
        selectedGroups: miscellaneous.selectedGroups ?? selectedGroups ?? [],
        mailAction: miscellaneous.mailAction ?? '',
      };

      return result;
    }

    // Fallback to defaults when no context values exist
    const defaults = miscellaneousFields
      ? Object.keys(miscellaneousFields).reduce(
          (acc, key) => {
            const field = miscellaneousFields[key];
            if (field.defaultValue !== undefined) {
              acc[key] = field.defaultValue;
            }
            return acc;
          },
          {} as Record<string, any>,
        )
      : {};

    defaults['email'] = email ?? [];
    defaults['phone'] = phone ?? '';
    defaults['countryCode'] = countryCode ?? '';
    defaults['selectedGroups'] = selectedGroups ?? [];
    defaults['mailAction'] = '';

    return defaults;
  }, [miscellaneousFields, miscellaneous]);

  const form = useForm<ReportTemplateMiscellaneousValues>({
    resolver: zodResolver(miscellaneousSchema),
    defaultValues: defaultValues,
  });

  // Track active tab to detect switches - only sync when switching TO wizard
  const { activeTab } = useCreateReportContext();
  const previousActiveTabRef = useRef<string | null>(null);

  // Reset form when switching TO wizard mode or when defaultValues change significantly
  // This is the ONLY place we sync context -> form (one-way sync)
  useEffect(() => {
    const justSwitchedToWizard =
      previousActiveTabRef.current === 'quick' && activeTab === 'wizard';

    // Reset form when switching to wizard mode OR when context values change
    // (e.g., when coming back to this step)
    if (justSwitchedToWizard || previousActiveTabRef.current === null) {
      form.reset(defaultValues);
    }

    previousActiveTabRef.current = activeTab;
  }, [activeTab, defaultValues, form]);

  const [fieldEnabledState, setFieldEnabledState] = useState<FieldEnabledState>(
    () => {
      const initialState: FieldEnabledState = {};
      Object.keys(miscellaneousFields).forEach((fieldName) => {
        const fieldInfo = miscellaneousFields[fieldName];
        initialState[fieldName] =
          !fieldInfo.enabledOn ||
          form.getValues()[fieldInfo.enabledOn.field] ===
            fieldInfo.enabledOn.value;
      });
      return initialState;
    },
  );

  useWatchColorBy(form);

  useEffect(() => {
    const subscription = form.watch((values) => {
      setWatchedValues(values);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  useEffect(() => {
    if (!miscellaneousFields) return;

    let hasChanged = false;
    const updatedState: FieldEnabledState = { ...fieldEnabledState };

    Object.keys(miscellaneousFields).forEach((fieldName) => {
      const fieldInfo = miscellaneousFields[fieldName];
      // Check if watchedValues has the key and if it's equal to the expected value
      const isEnabled =
        !fieldInfo.enabledOn ||
        watchedValues[fieldInfo.enabledOn.field] === fieldInfo.enabledOn.value;
      if (updatedState[fieldName] !== isEnabled) {
        updatedState[fieldName] = isEnabled;
        hasChanged = true;

        if (!isEnabled && fieldEnabledState[fieldName]) {
          form.setValue(fieldName, undefined);
        } else if (isEnabled && !fieldEnabledState[fieldName]) {
          // When re-enabling a field, use the value from context if it exists, otherwise use default
          const contextValue = miscellaneous[fieldName];
          const valueToSet =
            contextValue !== undefined ? contextValue : fieldInfo.defaultValue;
          form.setValue(fieldName, valueToSet);
        }
      }
    });

    if (hasChanged) {
      setFieldEnabledState(updatedState);
    }
  }, [
    form,
    watchedValues,
    fieldEnabledState,
    miscellaneousFields,
    miscellaneous,
  ]);

  const scrollAreaParentRef = useRef<HTMLDivElement>(null);
  const [scrollAreaHeight, setScrollAreaHeight] = useState<number | null>(null);

  useEffect(() => {
    if (scrollAreaParentRef.current) {
      setScrollAreaHeight(scrollAreaParentRef.current.offsetHeight);
    }
  }, []);

  const phoneActive = false;

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = async (data: ReportTemplateMiscellaneousValues) => {
    if (data?.shapeId) {
      data.shape = await shapeFetch(data.shapeId);
    }
    setMiscellaneous(data);
    stepperProps.nextStep();
  };

  const groupedFields = groupFieldsByDependency(miscellaneousFields);

  return (
    <div className="grid grid-rows-[auto_1fr_auto] h-full">
      <StepperHeadline
        headerText={messages.reportsCreateReportMiscellaneousTitle}
        subHeaderText={messages.reportsCreateReportMiscellaneousSubtitle}
      ></StepperHeadline>
      <Form {...form}>
        <form className="flex flex-col space-y-8 pt-4 h-full flex-grow overflow-hidden">
          <div className="h-[320px] overflow-hidden" ref={scrollAreaParentRef}>
            <ScrollArea
              style={{
                height: `${scrollAreaHeight}px`,
              }}
              className="flex-grow overflow-hidden pr-4 max-h-full"
            >
              {prefilledFieldsMap && (
                <PrefillSelect
                  prefilledFieldsMap={prefilledFieldsMap}
                  form={form}
                />
              )}
              <div className="space-y-6 px-px">
                {groupedFields.length > 0 ? (
                  groupedFields.map((group) => (
                    <FieldGroupRenderer
                      key={group.join('-')}
                      group={group}
                      miscellaneousFields={miscellaneousFields}
                      fieldEnabledState={fieldEnabledState}
                      form={form}
                      prefix={false}
                      dateRange={dateRange}
                    />
                  ))
                ) : (
                  <Label>This report does not have any specific fields.</Label>
                )}
              </div>
              <Separator className="my-6"></Separator>
              <div className="space-y-6 mb-2 px-px">
                <Label className="text-md">
                  {messages.reportsCreateReportMiscellaneousNotifyLabel}
                </Label>
                <FormField
                  key="email"
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="px-px">
                      <div className="flex justify-between">
                        <FormLabel>
                          {
                            messages.reportsCreateReportMiscellaneousNotifyEmailLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip className="mr-2">
                          {
                            messages.reportsCreateReportMiscellaneousNotifyEmailTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl>
                        <ListInput
                          value={field.value || []}
                          onChange={field.onChange}
                          placeholder={
                            messages.reportsCreateReportMiscellaneousNotifyEmailPlaceholder ||
                            'Enter email and press Enter'
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  key="mailAction"
                  control={form.control}
                  name="mailAction"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex justify-between">
                        <FormLabel>
                          {messages.reportsCreateReportMiscellaneousMailActionLabel ||
                            'Email content'}
                        </FormLabel>
                        <InfoIconTooltip className="mr-2">
                          {messages.reportsCreateReportMiscellaneousMailActionTooltip ||
                            'Choose what to include in the email when the report completes'}
                        </InfoIconTooltip>
                      </div>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                messages.reportsCreateReportMiscellaneousMailActionPlaceholder ||
                                'Select email content'
                              }
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent key={`mail-options-${mailActionOptions.length}`}>
                          {mailActionOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  key="selectedGroups"
                  control={form.control}
                  name="selectedGroups"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex justify-between">
                        <FormLabel>
                          {
                            messages.reportsCreateReportMiscellaneousShareWithLabel
                          }
                        </FormLabel>
                        <InfoIconTooltip className="mr-2">
                          {
                            messages.reportsCreateReportMiscellaneousShareWithTooltip
                          }
                        </InfoIconTooltip>
                      </div>
                      <FormControl onChange={() => form.clearErrors()}>
                        <GroupMultiSelect {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {phoneActive && (
                  <div>
                    <div className="flex justify-between mb-2">
                      <FormLabel>
                        {
                          messages.reportsCreateReportMiscellaneousNotifyPhoneLabel
                        }
                      </FormLabel>
                      <InfoIconTooltip>
                        {
                          messages.reportsCreateReportMiscellaneousNotifyPhoneTooltip
                        }
                      </InfoIconTooltip>
                    </div>
                    <div className="flex w-full">
                      <div className="relative w-24">
                        <PiFlag className="absolute left-3 top-5 transform -translate-y-1/2 text-muted-foreground" />
                        <FormField
                          key="countryCode"
                          control={form.control}
                          name="countryCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl onChange={() => form.clearErrors()}>
                                <Input
                                  className="pl-9 rounded-r-none"
                                  type="text"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="relative w-full">
                        <PiPhone className="absolute left-3 top-5 transform -translate-y-1/2 text-muted-foreground" />
                        <FormField
                          key="phone"
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl onChange={() => form.clearErrors()}>
                                <Input
                                  className="pl-9 rounded-l-none"
                                  type="text"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </form>
      </Form>
      <StepperFooter
        onPrevious={onPrevious}
        onNext={form.handleSubmit(onNext)}
        stepperProps={stepperProps}
      />
    </div>
  );
};
